import type {Dispatch, RefObject, SetStateAction} from 'react';

import {keyframes} from '@emotion/react';
import styled from '@emotion/styled';
import {media, spacing} from '@nfq/react-grid';
import * as motion from 'motion/react-m';

import {Button} from 'UI/components/action/Button/Button';

import {ModalEnterExitAnimation} from 'UI/animations/modals';

import {useModalAnimation} from './useModalAnimation';
import {transient} from 'Application/configs/emotionTransientProps';
import {Close} from 'Images/icons';

import type {WithChildren} from 'types/global';

/**
 * The `modalFactory` function is a factory function that generates a `Modal` component.
 * This modal can be customized with size and testId props, handles opening and closing actions,
 * and provides a close button to dismiss the modal. It uses animation for enter and exit transitions.
 *
 * @param state The reference to the state that controls the visibility of the modal.
 * @returns A `Modal` component that renders a modal window with customizable size and content.
 *
 * @example
 * ```tsx
 * const Modal = modalFactory(useState(false)[1]);
 * <Modal testId="myModal">
 *   <p>Modal content</p>
 * </Modal>
 * ```
 */
const modalFactory = (state: RefObject<Dispatch<SetStateAction<boolean>> | null>) => {
    /**
     * Interface for the props used by the `Modal` component.
     * This component provides a customizable modal dialog, which can be resized and tested using a unique `testId`.
     */
    interface ComponentProps {
        /**
         * The `testId` property represents a unique identifier, usually in the form of a string, assigned to a component for testing purposes.
         * This property is crucial for uniquely identifying components during testing, allowing for more accurate and reliable tests.
         *
         * @default 'Modal'
         */
        testId?: string;
    }

    /**
     * The `Modal` component renders a modal window that can contain various child elements and handles opening and closing actions.
     * It uses animations for entering and exiting the modal, and provides a close button to dismiss the modal.
     *
     * @param props          The component props.
     * @param props.children The content to be displayed inside the modal.
     * @param props.testId   The `testId` property represents a unique identifier for testing purposes.
     * @returns A JSX element representing the modal window with content and close functionality.
     *
     * @example
     * ```tsx
     * <Modal testId="custom-modal">
     *   <p>Modal content goes here.</p>
     * </Modal>
     * ```
     */
    const Modal = ({children, testId = 'Modal'}: WithChildren<ComponentProps>) => {
        const {controls, modal, setOpen, showContent} = useModalAnimation(state);

        return (
            <Wrapper
                ref={modal}
                animate={controls}
                data-cy={testId}
                variants={ModalEnterExitAnimation}
            >
                <CloseWrapper onClick={() => setOpen(false)}>
                    <Button
                        as="button"
                        icon={Close}
                        variant="tertiary"
                        onClick={() => setOpen(false)}
                    />
                </CloseWrapper>
                {showContent && children}
            </Wrapper>
        );
    };

    Modal.displayName = 'Modal';
    Modal.defaultProps = {
        size: 'default',
        testId: undefined
    };

    return Modal;
};

export {modalFactory};

const fadeIn = keyframes`
    0% {
        opacity: 0;
    }
    100% {
        opacity: 1;
    }
`;

const Wrapper = styled(motion.dialog, transient)`
    align-content: stretch;
    background: ${({theme}) => theme.colors.neutral0};
    border: none;
    border-radius: 16px;
    height: fit-content;
    max-height: 90%;
    max-width: 90%;
    min-height: 20rem;
    opacity: 0;
    outline: none;
    overflow: hidden;
    padding: 0;
    transform: translateY(80px);
    width: 95%;

    &::backdrop {
        animation: ${fadeIn} 0.3s ease-in-out backwards;
        animation-direction: reverse;
        background-color: rgba(0, 0, 0, 0.7);
    }

    &[open]::backdrop {
        animation: ${fadeIn} 0.3s ease-in-out forwards;
    }

    ${media('md')} {
        height: 90%;
    }
`;

const CloseWrapper = styled('div', transient)`
    position: absolute;
    right: ${spacing(5)};
    top: ${spacing(5)};
    z-index: 10;
`;